import { Link, useParams } from "@tanstack/react-router";
import { ChevronRightIcon, LightbulbIcon, PaletteIcon } from "lucide-react";
import { useState } from "react";

import { isOpenSuggestion } from "../regulation/policy/suggestionsView";
import { useRecommendations } from "../../state/governance";
import { renameOrganization, useOrgs, type OrgView } from "../../state/orgs";
import { OrgLogo } from "../OrgSwitcher";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { EditCompanyContextDialog } from "./EditCompanyContextDialog";
import { BrowserLoginsOrganizationSection } from "./BrowserLoginsOrganizationSection";
import { SettingsCard, SettingsCardRow, SettingsPageHeader } from "./settingsCard";
import { SettingsPageContainer } from "./settingsLayout";
import { searchableSetting } from "./settingsSearch";
import { useOrgSettingsPage } from "./orgSettingsShared";

/** "complianceOfficer" / "compliance_officer" → "Compliance officer". */
export function formatRole(role: string): string {
  const spaced = role
    .replace(/[_-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .trim();
  return spaced.length > 0 ? spaced[0]!.toUpperCase() + spaced.slice(1) : role;
}

function roleLabel(org: OrgView, isGlobalAdmin: boolean): string {
  return org.role ? formatRole(org.role) : isGlobalAdmin ? "Global admin" : "Member";
}

/** A name as saved: inner whitespace collapsed, ends trimmed. */
export const normalizeOrgName = (value: string): string => value.replace(/\s+/g, " ").trim();

function RenameOrganizationDialog({
  org,
  open,
  onOpenChange,
}: {
  readonly org: OrgView;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const [draft, setDraft] = useState(org.name);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastOpen, setLastOpen] = useState(open);
  if (open !== lastOpen) {
    setLastOpen(open);
    if (open) {
      setDraft(org.name);
      setError(null);
    }
  }
  const name = normalizeOrgName(draft);
  const canSave = !isSaving && name.length > 0 && name !== org.name;

  const save = async () => {
    if (!canSave) return;
    setIsSaving(true);
    const failure = await renameOrganization(org.id, name);
    setIsSaving(false);
    setError(failure);
    if (!failure) onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && isSaving) return;
        onOpenChange(next);
      }}
    >
      <DialogPopup className="max-w-md">
        <form
          className="flex min-h-0 flex-col"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <DialogHeader>
            <DialogTitle>Rename organization</DialogTitle>
            <DialogDescription>
              Shown across the workspace, in the organization switcher and on app.amoreg.com.
            </DialogDescription>
          </DialogHeader>
          <DialogPanel className="space-y-3">
            <label className="block space-y-1.5">
              <span className="text-sm font-medium">Organization name</span>
              <Input
                autoFocus
                value={draft}
                disabled={isSaving}
                aria-invalid={name.length === 0 || undefined}
                onChange={(event) => {
                  setDraft(event.currentTarget.value);
                  setError(null);
                }}
              />
            </label>
            {name.length === 0 ? (
              <p className="text-xs text-destructive">The organization needs a name.</p>
            ) : null}
            {error ? (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            ) : null}
          </DialogPanel>
          <DialogFooter>
            <Button type="button" variant="ghost" disabled={isSaving} onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSave}>
              {isSaving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogPopup>
    </Dialog>
  );
}

/**
 * Inspector can propose wording for this profile. A sweep reads the whole
 * library, so it is the one agent placed to notice what the profile does not
 * say — the business the documents evidence, the regulator they answer to.
 *
 * The proposal is read and decided on the Suggestions page, with every other
 * suggestion; this is only a pointer. But an admin who came here to edit the
 * profile is exactly the person who should know one is waiting, and without
 * this they would have to already be looking for it. Members see it too: the
 * page is theirs to read, and only the deciding is an admin's.
 */
function CompanyProfileSuggestions({ organizationId }: { readonly organizationId: string }) {
  const { snapshot } = useRecommendations(organizationId);
  const orgSegment = useParams({
    strict: false,
    select: (params) => (params as { org?: string }).org ?? "",
  });
  const waiting = (snapshot?.recommendations ?? []).filter(
    (item) => item.kind === "org_context" && isOpenSuggestion(item),
  );
  if (waiting.length === 0) return null;
  return (
    <Link
      to="/$org/compliance/suggestions"
      params={{ org: orgSegment }}
      className="mt-1.5 flex w-fit max-w-full items-center gap-1.5 rounded-lg border border-warning/30 bg-warning/8 px-2.5 py-1.5 text-xs text-foreground hover:bg-warning/12"
    >
      <LightbulbIcon aria-hidden className="size-3.5 shrink-0 text-warning" />
      <span>
        Inspector has proposed{" "}
        {waiting.length === 1 ? "wording" : `${waiting.length} additions`} for this profile — read{" "}
        {waiting.length === 1 ? "it" : "them"} in Suggestions
      </span>
    </Link>
  );
}

/**
 * `/$org/settings/organization` — Organization → General: who this
 * organization is (name, mark, address in the URL) and the context eve
 * carries into every thread for it. Switching tenants belongs to the nav
 * rail, so this page is only ever about the selected one; members, usage
 * and branding have their own pages.
 */
export function OrganizationSettingsPanel() {
  const { viewer } = useOrgs();
  const { org, profile, canManage, save, fallback } = useOrgSettingsPage("General");
  const [contextDialogOpen, setContextDialogOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const orgSegment = useParams({
    strict: false,
    select: (params) => (params as { org?: string }).org ?? "",
  });

  if (fallback) return fallback;
  const current = org!;
  const context = profile!.organizationContext ?? "";
  const contextWords = context.trim().length === 0 ? 0 : context.trim().split(/\s+/).length;

  return (
    <SettingsPageContainer className="gap-10">
      <SettingsPageHeader
        title="General"
        description="Who this organization is, and what eve should know about it in every thread."
      />

      <SettingsCard id="organization" title="Profile">
        <div className="flex items-center gap-4 px-4 py-4 sm:px-5">
          <OrgLogo org={current} className="size-12 rounded-lg" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-base font-medium text-foreground">{current.name}</p>
            <p className="truncate text-xs text-muted-foreground">
              {current.slug}
            </p>
          </div>
          <Badge variant="outline">{roleLabel(current, viewer.isGlobalAdmin)}</Badge>
        </div>

        <SettingsCardRow
          {...searchableSetting("organization-name")}
          description={
            canManage
              ? "Shown across the workspace and on app.amoreg.com."
              : "Only organization admins can rename it."
          }
          control={
            <div className="flex items-center gap-3">
              <span className="max-w-56 truncate text-sm text-muted-foreground">{current.name}</span>
              {canManage ? (
                <Button size="sm" variant="outline" onClick={() => setRenameOpen(true)}>
                  Rename
                </Button>
              ) : null}
            </div>
          }
        />

        <SettingsCardRow
          id="organization-branding"
          title="Logo and branding"
          description="The organization's mark, colours and tone of voice for generated documents."
          control={
            orgSegment ? (
              <Button
                size="sm"
                variant="outline"
                render={
                  <Link to="/$org/settings/$section" params={{ org: orgSegment, section: "branding" }} />
                }
              >
                <PaletteIcon />
                Branding
                <ChevronRightIcon className="-me-1" />
              </Button>
            ) : null
          }
        />
      </SettingsCard>

      <SettingsCard
        title="Context for eve"
        description="Written into eve's prompt for every thread in this organization."
      >
        <SettingsCardRow
          {...searchableSetting("company-profile")}
          description="What this organization does, who it answers to, and anything eve should assume."
          status={
            <>
              {contextWords > 0
                ? `${contextWords.toLocaleString()} words`
                : "Nothing written yet — eve works from the organization name alone."}
              <CompanyProfileSuggestions organizationId={current.id} />
            </>
          }
          control={
            <Button
              size="sm"
              variant="outline"
              disabled={!canManage}
              onClick={() => setContextDialogOpen(true)}
            >
              {canManage ? "Edit context" : "View context"}
            </Button>
          }
        />
      </SettingsCard>

      <BrowserLoginsOrganizationSection
        organizationId={current.id}
        organizationName={current.name}
        canManage={canManage}
      />

      <RenameOrganizationDialog
        key={current.id}
        org={current}
        open={renameOpen}
        onOpenChange={setRenameOpen}
      />

      <EditCompanyContextDialog
        open={contextDialogOpen}
        onOpenChange={setContextDialogOpen}
        value={context}
        // The profile this dialog opened on goes with the change: eve can
        // append to the same column while it sits open, and the server
        // refuses rather than posting over an addition nobody here saw.
        onSave={(next) =>
          save({
            organizationContext: next.trim() || null,
            expectedOrganizationContext: profile!.organizationContext ?? null,
          })
        }
      />
    </SettingsPageContainer>
  );
}
